import { joinList } from './format';
import { APP_NAME } from './platform';

export interface AuthErrorMessage {
  title: string;
  body: string;
}

/**
 * Plain-language explanations for every way a sign-in can fail. `providers`
 * names the ways to sign in that the server offers, such as "Google", and
 * only those are mentioned; it is empty while they are not known. `password`:
 * the server also offers an email address and a password.
 */
export function describeAuthError(code: string, providers: readonly string[] = [], opts: { password?: boolean } = {}): AuthErrorMessage {
  const all = [...providers, ...(opts.password ? ['your email address and password'] : [])];
  const ways = joinList(all);
  // Another way in, for a message about one that did not work.
  const others = (except: string) => joinList(all.filter((w) => w !== except));
  switch (code) {
    case 'cancelled':
      return { title: 'Sign-in was cancelled', body: 'You closed the sign-in page before finishing. Nothing was changed. Choose an account to try again.' };
    case 'state_invalid':
      return {
        title: 'That sign-in attempt expired',
        body: 'Sign-in has to be finished within 10 minutes, in the same browser where you started it. Try again.',
      };
    case 'provider_unavailable':
      return {
        title: "We couldn't reach the sign-in service",
        body: `${providers.length > 0 ? joinList(providers) : 'The service you sign in with'} did not respond. This is usually temporary. Wait a moment and try again.`,
      };
    case 'email_missing':
      return {
        title: 'Your account has no email address',
        body: `${APP_NAME} needs an email address to create your account, and the account you chose did not share one. Try a different account.`,
      };
    case 'email_not_verified_conflict':
      return {
        title: 'This email already has an account',
        body: `An account with this email address exists, but the sign-in you just used could not confirm that the address is yours. Sign in the way you did originally${all.length > 1 ? ` (${ways})` : ''}.`,
      };
    case 'rate_limited':
      return { title: 'Too many sign-in attempts', body: 'Wait a few minutes, then try again.' };
    case 'code_invalid':
      return { title: 'That sign-in link expired', body: 'Start sign-in again from the app.' };
    case 'apple_sheet_expired':
      return { title: 'That sign-in attempt expired', body: 'Sign in with Apple has to be finished within 10 minutes. Try again.' };
    case 'network':
      return { title: "Can't reach the server", body: 'Check your connection and try again.' };
    case 'token_invalid':
      return { title: 'Apple could not confirm this sign-in', body: 'Nothing was changed. Try again.' };
    case 'apple_sheet_failed':
      return {
        title: 'Apple sign-in did not work',
        body: `This iPhone or iPad could not finish Sign in with Apple. Check that it is signed in to an Apple Account in Settings, then try again.${others('Apple') ? ` You can also continue with ${others('Apple')}.` : ''}`,
      };
    case 'account_suspended':
      return {
        title: 'This account is suspended',
        body: 'It was suspended for breaking our Terms. The email we sent says why. If you think it’s a mistake, write to us from the Help page.',
      };
    default:
      return { title: 'Sign-in did not work', body: 'Something went wrong while signing you in. Nothing was changed. Try again.' };
  }
}
